/**
 * ProductSyncJobList - API collection for product sync jobs
 */

import { get, post, del } from '@/services/http';
import {
  ProductSyncJob,
  ProductSyncJobModel,
  InitiateClearDeletedResponse,
  GetSyncStatusResponse,
  GetActiveJobResponse,
  GetSyncHistoryResponse,
  CancelSyncResponse
} from '@/models/product_sync_job';
import List from './list';

export class ProductSyncJobList extends List<ProductSyncJobModel> {
  /**
   * Initiate clear deleted products process
   * @returns Promise with job ID and status
   */
  public async initiateClearDeleted(): Promise<InitiateClearDeletedResponse> {
    const res = await post<InitiateClearDeletedResponse>('/products/clear-deleted', {
      confirm: true
    });
    return res;
  }

  /**
   * Get status of a specific job
   * @param jobId - The job ID to check
   * @returns Promise with job details
   */
  public async getStatus(jobId: number): Promise<ProductSyncJobModel> {
    const res = await get<GetSyncStatusResponse>(`/products/sync-status/${jobId}`, {});
    const job = new ProductSyncJobModel();
    job.mapData(res.data);
    return job;
  }

  /**
   * Get the active job for the current merchant (if any)
   * @returns Promise with active job or null
   */
  public async getActiveJob(): Promise<ProductSyncJobModel | null> {
    const res = await get<GetActiveJobResponse>('/products/sync-status', {});
    if (!res.data) {
      return null;
    }
    const job = new ProductSyncJobModel();
    job.mapData(res.data);
    return job;
  }

  /**
   * Get sync history for the current merchant
   * @param limit - Maximum number of jobs to return
   * @returns Promise with list of jobs
   */
  public async getHistory(limit: number = 10): Promise<ProductSyncJobModel[]> {
    const res = await get<GetSyncHistoryResponse>('/products/sync-history', { limit });
    const jobs: ProductSyncJobModel[] = [];
    for (const jobData of res.data) {
      const job = new ProductSyncJobModel();
      job.mapData(jobData);
      jobs.push(job);
    }
    return jobs;
  }

  /**
   * Fetch and populate the list with sync history
   */
  public async fetch(limit: number = 10) {
    const jobs = await this.getHistory(limit);
    this.clear();
    for (const job of jobs) {
      this.add(job);
    }
  }

  /**
   * Cancel a running job
   * @param jobId - The job ID to cancel
   * @returns Promise with cancellation result
   */
  public async cancelJob(jobId: number): Promise<CancelSyncResponse> {
    const res = await del<CancelSyncResponse>(`/products/sync/${jobId}`, {});
    return res;
  }
}

// Export singleton instance
export const productSyncJobs = new ProductSyncJobList();
